import { useMemo } from "react";
// import { useState } from "react";
import styles from "./RetentionPage.module.scss";
import AnalyticsBars from "../../../../common/components/analytics-component/bar-list/AnalyticsBars";
import AnalyticsCard from "../../../../common/components/analytics-component/card/AnalyticsCard";
import AnalyticsHeader from "../../../../common/components/analytics-component/header/AnalyticsHeader";
import AnalyticsStat from "../../../../common/components/analytics-component/stat/AnalyticsStat";
import AnalyticsTrend from "../../../../common/components/analytics-component/trend/AnalyticsTrend";
import AnalyticsHeatmap from "../../../../common/components/analytics-component/heatmap/AnalyticsHeatmap";
// import { AnalyticsHeatmapRow } from "../../../../common/components/analytics-component/heatmap/AnalyticsHeatmap";
import AnalyticsLineChart from "../../../../common/components/analytics-component/line-chart/AnalyticsLineChart";
import AnalyticsMessage from "../../../../common/components/analytics-component/message/AnalyticsMessage";
// import AnalyticsSelect from "../../../../common/components/analytics-component/select/AnalyticsSelect";
import {
	AUTO_RECHARGE_ADOPTERS,
	AUTO_RECHARGE_NON_ADOPTERS,
	// FIRST_VEND_COHORTS,
	// PAYMENT_VEND_COHORTS,
	REACTIVATION_SOURCES,
	RETENTION_COLUMNS,
	vendRetentionRows,
} from "../Retention.constants";
import { useGetVendRetentionQuery } from "../../../../service/business-analytics-service";
import { AnalyticsPlatformParams } from "../../../../model/bps-admin/business-analytics";

// The cohort switch is commented out: the endpoint has one cohort definition — everyone
// who vended that week — so there is nothing to switch between yet.
//
// // Which activation event the cohorts are keyed on. This is the heatmap's own switch,
// // not a page filter — it changes what the grid counts, not the slice it counts over.
// const COHORT_OPTIONS = [
// 	{ value: "first-vend", label: "First vend" },
// 	{ value: "payment-vend", label: "Payment vend" },
// ];
//
// const COHORTS: Record<string, AnalyticsHeatmapRow[]> = {
// 	"first-vend": FIRST_VEND_COHORTS,
// 	"payment-vend": PAYMENT_VEND_COHORTS,
// };

type Props = {
	/** The page's filters — the section has none of its own. */
	filters: AnalyticsPlatformParams;
};

// Cohort retention in its own right; the overview draws the same grid.
// The charts under the heatmap land once there is an endpoint behind them.
const RetentionSection = ({ filters }: Readonly<Props>) => {
	// const [cohort, setCohort] = useState(COHORT_OPTIONS[0].value);

	const {
		data: vendRetention,
		isLoading: isLoadingRetention,
		isFetching: isRefetchingRetention,
		isError: isRetentionError,
		refetch: refetchRetention,
	} = useGetVendRetentionQuery(filters);

	const retentionRows = useMemo(() => vendRetentionRows(vendRetention), [vendRetention]);

	return (
		<>
			<AnalyticsHeatmap
				title="Vend retention by weekly cohort"
				subtitle="% who returned to buy electricity - the matters, not just app opens"
				// action={<AnalyticsSelect options={COHORT_OPTIONS} value={cohort} onChange={setCohort} />}
				columns={RETENTION_COLUMNS}
				rows={retentionRows}
				unit="%"
				loading={isLoadingRetention}
				error={isRetentionError}
				onRetry={refetchRetention}
				retrying={isRefetchingRetention}
			/>
			<div className={styles.chartRow}>
			<AnalyticsLineChart
				title="Retention by feature: auto-recharge"
				subtitle="Adopters vs non-adopters · the ROI case for energy management"
				mock
				labels={RETENTION_COLUMNS}
				series={[
					{ label: "Auto-recharge on", data: AUTO_RECHARGE_ADOPTERS, colorSlot: 2 },
					{ label: "No auto-recharge", data: AUTO_RECHARGE_NON_ADOPTERS, muted: true },
				]}
				filled={false}
			>
				<AnalyticsMessage tone="positive" title="Auto-recharge users retain ~19pt higher at W4">
					48% vs 29%. Fixing energy management isn&apos;t cosmetic — it&apos;s a retention lever.
				</AnalyticsMessage>
			</AnalyticsLineChart>
			{/* Composed here rather than as a component: the pieces are shared, the arrangement is not. */}
			<AnalyticsCard>
				<AnalyticsHeader title="Reactivation" subtitle="Dormant customers who returned to vend" mock />
				<div className={styles.statRow}>
					<AnalyticsStat
						label="Reactivated (30d)"
						value="3,180"
						note={<AnalyticsTrend value="12%" direction="up" caption="vs prior" />}
					/>
					<AnalyticsStat label="Still dormant" value="41,600" note="no vend in 30d+" />
				</div>
				<AnalyticsBars rows={REACTIVATION_SOURCES} />
			</AnalyticsCard>
			</div>
		</>
	);
};

export default RetentionSection;
